/**
 * TypeScript type definitions for Datatable system
 * Backend models reference: src/WebApp/Features/DataTable/
 */

// Backend: src/WebApp/Features/DataTable/DatatableDefinition.cs
// Discriminated union for client-side vs server-side datatables
export type DatatableDefinition = ClientDatatableDefinition | ServerDatatableDefinition;

// Client-side datatable - all data provided upfront
export interface ClientDatatableDefinition {
  Data: DatatableRow[]; // All row data provided in definition
  Columns: ColumnDefinition[];
  HasActionsColumn: boolean;
  TableActions: DatatableActionDefinition[];
  RowIdFieldName: string;
}

// Server-side datatable - data fetched via API
export interface ServerDatatableDefinition {
  Url: string; // API endpoint for data fetching
  Columns: ColumnDefinition[];
  HasActionsColumn: boolean;
  TableActions: DatatableActionDefinition[];
  RowIdFieldName: string;
  PageSizes: number[];
  DefaultPageSize: number;
  Sorts: SortDefinition[];
  DefaultPageNumber: number;
  InitialData?: DatatableRow[]; // Optional SSR first-page data
}

// Backend: src/WebApp/Features/DataTable/DatatableDefinition.cs
export enum ColumnFieldType {
  String = 0,
  Number = 1,
}

export interface ColumnDefinition {
  Title: string; // Localized display title
  FieldName: string; // Property name in row data (PascalCase)
  Sortable: boolean; // Whether column supports sorting
  FieldType?: ColumnFieldType; // Optional type for client-side sorting
  /** Whether text content is allowed to wrap. Defaults to true. */
  WrapAllowed: boolean;
  /** Optional minimum column width in pixels. */
  MinWidth?: number;
  /** Whether content in this column should be horizontally centered. Defaults to false. */
  Center?: boolean;
}

// Backend: src/WebApp/Features/DataTable/Models/ActionDefinition.cs
export enum DatatableActionType {
  Modal = 0,
  Command = 1,
  Link = 2,
}

// Base interface for all action types
interface BaseDatatableActionDefinition {
  ActionType: DatatableActionType;
  Name: string; // Display name (title attribute)
  IconName: string; // Iconify icon identifier (e.g., "mdi-eye")
  Url: string; // Action URL
  HttpMethod: string; // "Get" or "Post"
  IsPrimary: boolean; // Primary styling with name rendered
  MassActionSetting: MassActionSetting; // Mass action configuration
}

// Modal action - opens modal via modal-fetch.ts
export interface ModalActionDefinition extends BaseDatatableActionDefinition {
  ActionType: DatatableActionType.Modal;
  HttpMethod: 'Get'; // Always GET for modals
}

// Command action - POST with optional confirmation
export interface CommandActionDefinition extends BaseDatatableActionDefinition {
  ActionType: DatatableActionType.Command;
  HttpMethod: 'Post'; // Always POST for commands
  Confirmation: Confirmation;
}

// Link action - GET with optional new tab
export interface LinkActionDefinition extends BaseDatatableActionDefinition {
  ActionType: DatatableActionType.Link;
  HttpMethod: 'Get'; // Always GET for links
  OpenInNewTab: boolean;
}

// Discriminated union of all action types
export type DatatableActionDefinition =
  | ModalActionDefinition
  | CommandActionDefinition
  | LinkActionDefinition;

export interface MassActionSetting {
  IsMassAction: boolean; // Is this a mass action
  AllowWithoutSelection: boolean; // Can execute without selection
}

export interface Confirmation {
  RequiresConfirmation: boolean;
  ConfirmationMessage?: string;
}

// Backend: src/WebApp/Features/DataTable/Abstractions/DatatableRequest.cs
export interface DatatableRequest {
  PageSize: number;
  PageNumber: number; // 0-based
  Sorts: SortDefinition[];
}

export interface SortDefinition {
  FieldName: string;
  SortOrder: SortOrder;
}

export enum SortOrder {
  Ascending = 0,
  Descending = 1,
}

// Backend: src/WebApp/Features/DataTable/Abstractions/DatatableResponse.cs
export interface DatatableResponse<T extends DatatableRow> {
  Data: T[];
  Pagination: PaginationInfo | null;
  QueryString: string | null; // Update browser URL to this value
}

export interface PaginationInfo {
  TotalCount: number; // Total records matching filter
  PageNumber: number; // Current page (0-based)
}

// Backend: src/WebApp/Features/DataTable/Abstractions/DatatableRowBase.cs
export interface DatatableRow {
  Id: string; // Unique row identifier (string type)
  Actions: DatatableActionDefinition[];
  [key: string]: unknown; // Allow additional properties
}

// Backend: src/WebApp/Features/DataTable/Models/DatatableLocalizationModel.cs
export interface DatatableLocalization {
  Loading: string;
  EmptyListMessage: string;
  Previous?: string;
  Next?: string;
  First?: string;
  Last?: string;
  NoRowsSelected?: string;
  SortAscending?: string;
  SortDescending?: string;
  PageSizeLabel?: string;
}
